import { useState } from 'react';
import { extractErrorMessage } from '../../api/errors';
import { FeatureFlag, useFeatureFlags, useSetFeatureDefault } from '../../queries/featureFlags';
import { useToast } from '../../contexts/ToastContext';
import { Card, CardContent } from '../../components/ui/Card';
import { Spinner } from '../../components/ui/Spinner';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { cn } from '../../lib/utils';

const DATE_FORMATTER = new Intl.DateTimeFormat('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
const members = (n: number) => `${n} member${n === 1 ? '' : 's'}`;

/** Who a feature reaches: the default, and the members set one by one who keep their own setting. */
export function reach(flag: FeatureFlag): string {
  if (flag.defaultEnabled) {
    return flag.usersOff ? `On for everyone except ${members(flag.usersOff)} switched off one by one` : 'On for everyone';
  }
  return flag.usersOn ? `Off, except ${members(flag.usersOn)} switched on one by one` : 'Off for everyone';
}

/**
 * Each feature's default for every member who hasn't been given their own setting on their user page: turning one
 * on here reaches everyone at once (both brands share these). Members set one by one keep their own setting.
 */
export function FeatureFlags() {
  const { data, isLoading } = useFeatureFlags();
  const setDefault = useSetFeatureDefault();
  const { success, error } = useToast();
  const [pending, setPending] = useState<FeatureFlag | null>(null);

  if (isLoading || !data) return <Spinner />;

  const apply = (flag: FeatureFlag) => {
    const defaultEnabled = !flag.defaultEnabled;
    setDefault.mutate(
      { featureKey: flag.featureKey, defaultEnabled },
      {
        onSuccess: () => success(`${flag.name} turned ${defaultEnabled ? 'on' : 'off'} for everyone.`),
        onError: (err) => error(extractErrorMessage(err, `Failed to update ${flag.name}.`)),
      },
    );
  };

  const turningOn = pending ? !pending.defaultEnabled : false;

  return (
    <Card className="max-w-2xl">
      <CardContent className="divide-y divide-border py-1">
        {data.map((flag) => (
          <div key={flag.featureKey} className="flex items-center justify-between gap-4 py-3">
            <div className="min-w-0">
              <h3 className="font-semibold">{flag.name}</h3>
              {flag.description && <p className="text-sm text-muted-foreground">{flag.description}</p>}
              <p className="mt-1 text-xs text-muted-foreground">
                {reach(flag)}
                {flag.updatedAt && ` · Changed ${DATE_FORMATTER.format(new Date(flag.updatedAt))}`}
                {' · '}
                <code>{flag.featureKey}</code>
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={flag.defaultEnabled}
              aria-label={`${flag.name} for everyone`}
              onClick={() => setPending(flag)}
              disabled={setDefault.isPending}
              className={cn(
                'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50',
                flag.defaultEnabled ? 'bg-primary' : 'bg-muted-foreground/40',
              )}
            >
              <span className={cn('inline-block h-4 w-4 transform rounded-full bg-white transition-transform', flag.defaultEnabled ? 'translate-x-6' : 'translate-x-1')} />
            </button>
          </div>
        ))}
      </CardContent>

      <ConfirmDialog
        open={pending != null}
        onOpenChange={(open) => !open && setPending(null)}
        title={pending ? `Turn ${pending.name} ${turningOn ? 'on' : 'off'} for everyone?` : ''}
        description={
          pending
            ? turningOn
              ? `Every member on TippingIQ and PlatinumIQ gets ${pending.name} straight away${pending.usersOff ? `, except ${members(pending.usersOff)} switched off one by one` : ''}.`
              : `${pending.name} goes away for every member on TippingIQ and PlatinumIQ${pending.usersOn ? `, except ${members(pending.usersOn)} switched on one by one` : ''}.`
            : ''
        }
        confirmLabel={turningOn ? 'Turn on' : 'Turn off'}
        destructive={!turningOn}
        busy={setDefault.isPending}
        onConfirm={() => {
          const flag = pending;
          setPending(null);
          if (flag) apply(flag);
        }}
      />
    </Card>
  );
}
